import React, { useEffect, useRef, useState, useCallback } from 'react';
import { testimonials } from '../data/mock';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Star, Quote, Send, MessageSquare, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animated star rating component
const StarRating = ({ rating, setRating, interactive = false, size = 'md' }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const sizes = { sm: 'w-4 h-4', md: 'w-7 h-7', lg: 'w-9 h-9' };

  return (
    <div className="flex gap-1.5">
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = star <= (interactive ? (hoverRating || rating) : rating);
        return (
          <button
            key={star}
            type={interactive ? 'button' : undefined}
            disabled={!interactive}
            onClick={() => interactive && setRating(star)}
            onMouseEnter={() => interactive && setHoverRating(star)}
            onMouseLeave={() => interactive && setHoverRating(0)}
            className={`star-rating-btn transition-all duration-300 ${interactive ? 'cursor-pointer hover:scale-125' : 'cursor-default'} ${filled ? 'star-filled' : ''}`}
            style={{ animationDelay: `${star * 60}ms` }}
          >
            <Star
              className={`${sizes[size]} transition-all duration-300 ${
                filled
                  ? 'fill-amber-400 text-amber-400 drop-shadow-[0_0_6px_rgba(251,191,36,0.4)]'
                  : 'text-slate-200 hover:text-amber-200'
              }`}
            />
          </button>
        );
      })}
    </div>
  );
};

// Single feedback card
const FeedbackCard = ({ feedback, isNew = false }) => (
  <Card className={`bg-white border-slate-100 hover:border-teal-100 hover:shadow-lg transition-all duration-500 hover:-translate-y-0.5 ${isNew ? 'feedback-new-entry' : ''}`}>
    <CardContent className="p-6">
      <div className="mb-4">
        <Quote className="w-8 h-8 text-teal-100" />
      </div>
      <StarRating rating={feedback.rating} size="sm" />
      <p className="text-slate-600 leading-relaxed mt-3 mb-5 text-sm min-h-[60px]">
        "{feedback.text || feedback.message}"
      </p>
      <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold text-xs">
          {feedback?.name?.charAt(0) || ""}
        </div>
        <div>
          <p className="font-semibold text-slate-900 text-sm">{feedback.name}</p>
          <p className="text-xs text-slate-400">{feedback.role || 'Student/Parent'}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export const Feedbacks = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [liveFeedbacks, setLiveFeedbacks] = useState([]);
  const [formData, setFormData] = useState({ name: '', role: 'Student', rating: 0, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Combine static testimonials with live feedbacks
  const allFeedbacks = [...liveFeedbacks, ...testimonials];
  const [page, setPage] = useState(0);
  const perPage = 3;
  const totalPages = Math.ceil(allFeedbacks.length / perPage);
  const currentFeedbacks = allFeedbacks.slice(page * perPage, (page + 1) * perPage);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // Fetch live feedbacks
  const fetchFeedbacks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/feedbacks`);
      setLiveFeedbacks(res.data);
    } catch (err) {
      console.error('Failed to fetch feedbacks', err);
    }
  }, []);

  useEffect(() => {
    fetchFeedbacks();
    // Poll for realtime updates every 8 seconds
    const interval = setInterval(fetchFeedbacks, 8000);
    return () => clearInterval(interval);
  }, [fetchFeedbacks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Please enter your name'); return; }
    if (formData.rating === 0) { toast.error('Please select a rating'); return; }
    if (!formData.message.trim()) { toast.error('Please write a message'); return; }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/feedbacks`, {
        name: formData.name,
        role: formData.role,
        rating: formData.rating,
        message: formData.message
      });
      toast.success('Thank you for your feedback!');
      setFormData({ name: '', role: 'Student', rating: 0, message: '' });
      setShowForm(false);
      setPage(0);
      await fetchFeedbacks();
    } catch (err) {
      console.error('Feedback error:', err);
      toast.error('Failed to submit. Please try again.');
    }
    setIsSubmitting(false);
  };

  return (
    <section id="feedbacks" ref={ref} className="relative py-24 lg:py-32 bg-white overflow-hidden">
      {/* Background */}
      <div className="absolute top-20 left-[5%] w-64 h-64 bg-teal-100/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-[5%] w-80 h-80 bg-amber-100/20 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center max-w-2xl mx-auto mb-12 scroll-fade-in ${isVisible ? 'is-visible' : ''}`}>
          <Badge className="bg-amber-50 text-amber-700 border-amber-200 px-4 py-1.5 text-sm font-medium mb-6">
            Feedbacks
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-5">
            What People{' '}
            <span className="text-teal-600">Are Saying</span>
          </h2>
          <p className="text-lg text-slate-500 mb-6">
            Real stories from real students and parents. Share your experience too!
          </p>

          {/* Add Feedback Toggle */}
          <Button
            onClick={() => setShowForm(!showForm)}
            className={`rounded-full gap-2 font-semibold transition-all duration-300 ${
              showForm
                ? 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                : 'bg-teal-500 hover:bg-teal-400 text-white shadow-lg shadow-teal-500/20 hover:shadow-teal-400/30 hover:-translate-y-0.5'
            }`}
          >
            {showForm ? 'Cancel' : (<><Sparkles className="w-4 h-4" /> Share Your Feedback</>)}
          </Button>
        </div>

        {/* Feedback Form */}
        <div className={`feedback-form-wrapper ${showForm ? 'feedback-form-open' : 'feedback-form-closed'}`}>
          <Card className="max-w-2xl mx-auto mb-14 border-teal-100 shadow-xl shadow-teal-500/5 overflow-hidden">
            <div className="h-1 w-full bg-gradient-to-r from-teal-400 to-amber-400" />
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Write a Review</h3>
                  <p className="text-sm text-slate-500">Help others with your experience</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Your Name *</label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter your name"
                      className="h-12 rounded-xl border-slate-200 focus:border-teal-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">You are a</label>
                    <div className="flex gap-2">
                      {['Student', 'Parent'].map((role) => (
                        <button
                          key={role}
                          type="button"
                          onClick={() => setFormData({ ...formData, role })}
                          className={`flex-1 h-12 rounded-xl font-medium text-sm transition-all duration-300 ${
                            formData.role === role
                              ? 'bg-teal-50 text-teal-700 border-2 border-teal-300'
                              : 'bg-slate-50 text-slate-500 border-2 border-transparent hover:border-slate-200'
                          }`}
                        >
                          {role}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Star Rating */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">Your Rating *</label>
                  <StarRating rating={formData.rating} setRating={(r) => setFormData({ ...formData, rating: r })} interactive size="lg" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Your Message *</label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us about your experience at ENIAC..."
                    rows={3}
                    className="rounded-xl border-slate-200 focus:border-teal-300 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-12 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold shadow-lg shadow-teal-500/15 transition-all duration-300 disabled:opacity-60"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Submit Feedback
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Feedback cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-10">
          {currentFeedbacks.map((fb, index) => (
            <div
              key={fb.id || index}
              className={`scroll-fade-in ${isVisible ? 'is-visible' : ''}`}
              style={{ transitionDelay: `${index * 120}ms` }}
            >
              <FeedbackCard feedback={fb} isNew={!!fb.createdAt} />
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="rounded-full w-10 h-10 border-slate-200 hover:bg-teal-50 hover:border-teal-200 disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  i === page ? 'bg-teal-500 w-8' : 'bg-slate-300 hover:bg-slate-400'
                }`}
              />
            ))}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page === totalPages - 1}
              className="rounded-full w-10 h-10 border-slate-200 hover:bg-teal-50 hover:border-teal-200 disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};
